import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SectionHeader, Reveal } from "./primitives";
import { PropertyDealCard, PropertyDealCardSkeleton } from "./PropertyDealCard";
import { DealScoreGauge } from "../DealScoreGauge";
import { trackCTAClick } from "../../lib/tracking";

const QUICK_LINKS = [
  { to: "/top-deals", label: "Top Deals", testId: "freshness-link-top-deals" },
  { to: "/best-deals-under-300k", label: "Under $300K", testId: "freshness-link-under-300k" },
  { to: "/top-multifamily-deals", label: "Multifamily", testId: "freshness-link-multifamily" },
  { to: "/best-cash-flow-deals", label: "Best Cash Flow", testId: "freshness-link-cash-flow" },
];

export function TopDealsSection({ deals, loading, error, onOpenDeal, onSeeAll }) {
  return (
    <section className="rd-section bg-rd-offwhite" data-testid="top-deals-section" id="top-deals">
      <div className="rd-container">
        <Reveal>
          <SectionHeader
            eyebrow="Real opportunities. Real numbers."
            title="Today's Best Investment Deals"
            action={
              <button type="button" onClick={onSeeAll} className="rd-link inline-flex items-center gap-1.5 text-base min-h-[44px]" data-testid="see-all-deals">
                See All Investment Deals <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
            }
          />
        </Reveal>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading && [0, 1, 2].map((i) => <PropertyDealCardSkeleton key={i} />)}
          {!loading && deals.map((deal, i) => (
            <Reveal key={deal?.property?.id || i} delay={i * 80} className="min-w-0">
              <PropertyDealCard deal={deal} onOpen={() => onOpenDeal(deal)} testId={`deal-card-${i}`} />
            </Reveal>
          ))}
          {!loading && deals.length === 0 && (
            <div className="md:col-span-2 lg:col-span-3 rd-card p-10 text-center" data-testid="top-deals-empty">
              <p className="text-lg font-bold text-rd-text">{error ? "Live deals are temporarily unavailable." : "New deals are being analyzed right now."}</p>
              <p className="mt-1 text-sm text-rd-text-2">Browse the full Top Deals page or search any address to run your own analysis.</p>
              <button type="button" onClick={onSeeAll} className="rd-btn rd-btn-navy mt-5">Browse Top Deals</button>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="freshness-strip">
          <span className="text-sm text-rd-text-2 mr-1">Browse by category:</span>
          {QUICK_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => trackCTAClick("freshness_strip", l.to)}
              className="inline-flex items-center gap-1 min-h-[40px] px-3.5 rounded-lg border border-rd-border bg-white text-sm font-semibold text-rd-text hover:border-rd-green hover:text-rd-green transition-colors"
              data-testid={l.testId}
            >
              {l.label} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

const FACTORS = [
  { label: "Cash Flow", weight: 30, desc: "Monthly income after all expenses" },
  { label: "Cap Rate", weight: 20, desc: "Annual return on property value" },
  { label: "1% Rule", weight: 15, desc: "Rent-to-price screening ratio" },
  { label: "Price:Rent", weight: 15, desc: "Purchase price vs. annual rent" },
  { label: "Market Data", weight: 10, desc: "Local rent trends and vacancy" },
  { label: "Condition", weight: 10, desc: "Age, repairs, and maintenance" },
];

export function DealScoreSection({ onLearnMore }) {
  return (
    <section className="rd-section bg-rd-navy rd-grain text-white" data-testid="scoring-trust-section">
      <div className="rd-container relative">
        <Reveal>
          <SectionHeader
            dark
            eyebrow="A smarter way to evaluate properties"
            title={<>The RealixData Deal Score<sup className="text-base align-super ml-1 text-white/50">™</sup></>}
            subtitle="One score. The metrics that matter. RealixData evaluates a property's investment fundamentals and converts them into an easy-to-compare 0–100 score, so you can rank opportunities in seconds."
          />
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
          <Reveal className="rd-card-dark p-8 sm:p-10">
            <div className="grid grid-cols-3 gap-2 place-items-center sm:hidden">
              <DealScoreGauge score={87} size="md" dark />
              <DealScoreGauge score={64} size="md" dark />
              <DealScoreGauge score={38} size="md" dark />
            </div>
            <div className="hidden sm:grid grid-cols-3 gap-6 place-items-center">
              <DealScoreGauge score={87} size="lg" dark />
              <DealScoreGauge score={64} size="lg" dark />
              <DealScoreGauge score={38} size="lg" dark />
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4 text-center text-xs sm:text-sm text-white/60">
              <p><span className="font-bold text-white">75–100</span><br />Strong fundamentals</p>
              <p><span className="font-bold text-white">50–74</span><br />Worth a closer look</p>
              <p><span className="font-bold text-white">0–49</span><br />Higher-risk fundamentals</p>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <h3 className="rd-h3 text-xl text-white">Six weighted factors. No black box.</h3>
            <ul className="mt-6 space-y-4" aria-label="Deal Score weighting">
              {FACTORS.map((f) => (
                <li key={f.label}>
                  <div className="flex items-baseline justify-between text-sm mb-1.5">
                    <span className="font-semibold text-white">{f.label} <span className="text-white/45 font-normal ml-2 hidden sm:inline">{f.desc}</span></span>
                    <span className="font-mono font-bold text-rd-action">{f.weight}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden" role="presentation">
                    <div className="h-full rounded-full bg-rd-action" style={{ width: `${(f.weight / 30) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
            <button type="button" onClick={onLearnMore} className="rd-btn rd-btn-secondary-light mt-8" data-testid="scoring-learn-more">
              Learn How Deal Score Works <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
